import { useEffect, useState, type FormEvent } from 'react';
import { t } from '../../i18n';
import { formatCHF, parseDecimalInput } from '@werkdelta/shared';
import { Feld, Fehler, InfoTip } from '../../components/ui';
import { useEntityActions } from '../../hooks';
import { api } from '../../api';
import type { StepProps } from '../CasePage';

const CLAIM_TYPES = [
  'MINDERUNG',
  'SCHADENERSATZ',
  'MERKANTILER_MINDERWERT',
  'NUTZUNGSDAUERVERKUERZUNG',
  'WIRTSCHAFTLICHER_VERGLEICH',
];
const CLAIM_STATUSES = [
  'ANSPRUCHSGRUNDLAGE_VORGEPRUEFT',
  'VORAUSSETZUNGEN_TEILWEISE_OFFEN',
  'NUR_WIRTSCHAFTLICHES_VERGLEICHSSZENARIO',
  'ANSPRUCH_MOEGLICHERWEISE_AUSGESCHLOSSEN',
  'RECHTLICHE_PRUEFUNG_ERFORDERLICH',
  'NICHT_ALS_ANSPRUCHSBETRAG_VERWENDBAR',
];
const MWST = ['INKLUSIVE', 'EXKLUSIVE', 'UNKLAR'];
const SCENARIO_RELATIONS = ['ALTERNATIV', 'KUMULATIV', 'SUBSIDIAER', 'UNGEKLAERT'];
const SEVERITY_CLASS: Record<string, string> = {
  HINWEIS: 'hinweis',
  WARNUNG: 'warnung',
  KRITISCH: 'warnung',
};

/** Schritt 7: Anspruchsszenarien und Bewertungsauftrag (Auftrag 3.3, 7.7). */
export function Step7Bewertungsauftrag(props: StepProps) {
  return (
    <>
      <SzenarienKarte {...props} />
      <BewertungsauftragKarte {...props} />
    </>
  );
}

function SzenarienKarte({ caseId, data, reload, readonly }: StepProps) {
  const actions = useEntityActions(caseId, 'scenarios', reload);
  const [warnings, setWarnings] = useState<any[]>([]);
  const [form, setForm] = useState(leer());
  const [editId, setEditId] = useState<string | null>(null);

  function leer() {
    return {
      type: 'MINDERUNG',
      status: 'ANSPRUCHSGRUNDLAGE_VORGEPRUEFT',
      title: '',
      scenarioRelation: 'UNGEKLAERT',
      notes: '',
      defectFindingIds: [] as string[],
      propertySold: '',
      realizedLossDocumented: '',
      amountManual: '',
      repairCostComponent: '',
    };
  }

  useEffect(() => {
    api
      .get<{ warnings: any[] }>(`/api/v1/cases/${caseId}/scenarios/warnings`)
      .then((w) => setWarnings(w.warnings))
      .catch(() => setWarnings([]));
  }, [caseId, data]);

  function payload() {
    const amount = form.amountManual ? parseDecimalInput(form.amountManual) : undefined;
    const repair = form.repairCostComponent ? parseDecimalInput(form.repairCostComponent) : undefined;
    return {
      type: form.type,
      status: form.status,
      title: form.title,
      scenarioRelation: form.scenarioRelation,
      notes: form.notes || undefined,
      defectFindingIds: form.defectFindingIds,
      propertySold: form.propertySold === '' ? undefined : form.propertySold === 'ja',
      realizedLossDocumented:
        form.realizedLossDocumented === '' ? undefined : form.realizedLossDocumented === 'ja',
      amountManual: amount ?? undefined,
      repairCostComponent: repair ?? undefined,
    };
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    const ok = editId ? await actions.update(editId, payload()) : await actions.create(payload());
    if (ok) {
      setForm(leer());
      setEditId(null);
    }
  }

  function startEdit(s: any) {
    setEditId(s.id);
    setForm({
      type: s.type,
      status: s.status,
      title: s.title ?? '',
      scenarioRelation: s.scenarioRelation ?? 'UNGEKLAERT',
      notes: s.notes ?? '',
      defectFindingIds: (s.defects ?? []).map((d: any) => d.defectFindingId),
      propertySold: s.propertySold === null || s.propertySold === undefined ? '' : s.propertySold ? 'ja' : 'nein',
      realizedLossDocumented:
        s.realizedLossDocumented === null || s.realizedLossDocumented === undefined
          ? ''
          : s.realizedLossDocumented
            ? 'ja'
            : 'nein',
      amountManual: s.amountManual ?? '',
      repairCostComponent: s.repairCostComponent ?? '',
    });
  }

  const merkantil = form.type === 'MERKANTILER_MINDERWERT';

  return (
    <section className="karte">
      <h2>
        {t('step7.scenarios')}
        <InfoTip termKey="anspruchsszenario" />
      </h2>
      <p className="hinweis">{t('scenario.noAutoSum')}</p>
      {warnings.map((w, i) => (
        <div className={SEVERITY_CLASS[w.severity] ?? 'warnung'} key={i}>
          <span className="label">
            {t(`warning.severity.${w.severity}`)}
            {w.statusKey ? ` · ${t(w.statusKey)}` : ''}
          </span>
          <div>{t(w.key, w.params)}</div>
        </div>
      ))}
      {data.claimScenarios.length === 0 && <div className="leer-hinweis">{t('step7.scenariosEmpty')}</div>}
      {data.claimScenarios.map((s: any) => (
        <div className="eintrag" key={s.id}>
          <div className="eintrag-kopf">
            <strong>{s.title}</strong>
            <span className="badge tuerkis">{t(`scenario.type.${s.type}`)}</span>
            <span className="badge">{t(`scenario.status.${s.status}`)}</span>
            {s.scenarioRelation && s.scenarioRelation !== 'UNGEKLAERT' && (
              <span className="badge">{t(`scenario.relation.${s.scenarioRelation}`)}</span>
            )}
            {!readonly && (
              <span style={{ display: 'flex', gap: '0.4rem' }}>
                <button type="button" className="knopf-sekundaer knopf-klein" onClick={() => startEdit(s)}>
                  {t('common.edit')}
                </button>
                <button type="button" className="knopf-sekundaer knopf-klein" onClick={() => actions.remove(s.id)}>
                  {t('common.delete')}
                </button>
              </span>
            )}
          </div>
          <p className="meta">
            {t('step7.linkedDefects')}:{' '}
            {s.defects.length === 0
              ? '—'
              : s.defects
                  .map(
                    (link: any) =>
                      data.defectFindings.find((d: any) => d.id === link.defectFindingId)?.title ??
                      '?',
                  )
                  .join(', ')}
            {s.amountManual ? ` · ${t('step7.amountManual')}: ${formatCHF(s.amountManual)}` : ''}
            {s.repairCostComponent
              ? ` · ${t('step7.repairComponent')}: ${formatCHF(s.repairCostComponent)}`
              : ''}
          </p>
          {s.type === 'MERKANTILER_MINDERWERT' && (
            <p className="hinweis">{t('scenario.merkantil.reportText')}</p>
          )}
        </div>
      ))}
      {!readonly && (
        <form onSubmit={submit}>
          <h3>{editId ? t('step7.editScenario') : t('step7.addScenario')}</h3>
          <div className="feld-zeile">
            <Feld label={t('step7.scenarioType')} required>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                {CLAIM_TYPES.map((ct) => (
                  <option key={ct} value={ct}>
                    {t(`scenario.type.${ct}`)}
                  </option>
                ))}
              </select>
            </Feld>
            <Feld
              label={t('step7.scenarioStatus')}
              required
              termKey="anspruchsszenario"
              hint={merkantil ? t('step7.merkantilStatusFixed') : undefined}
            >
              <select
                value={merkantil ? 'RECHTLICHE_PRUEFUNG_ERFORDERLICH' : form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                disabled={merkantil}
              >
                {CLAIM_STATUSES.map((cs) => (
                  <option key={cs} value={cs}>
                    {t(`scenario.status.${cs}`)}
                  </option>
                ))}
              </select>
            </Feld>
          </div>
          <Feld label={t('step7.scenarioTitle')} required error={actions.fieldErrors['title']}>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
          </Feld>
          <Feld label={t('scenario.relationLabel')} required hint={t('scenario.relationHint')}>
            <select
              value={form.scenarioRelation}
              onChange={(e) => setForm({ ...form, scenarioRelation: e.target.value })}
            >
              {SCENARIO_RELATIONS.map((r) => (
                <option key={r} value={r}>
                  {t(`scenario.relation.${r}`)}
                </option>
              ))}
            </select>
          </Feld>
          <Feld label={t('step7.defectSelection')}>
            <select
              multiple
              size={Math.min(5, Math.max(2, data.defectFindings.length))}
              value={form.defectFindingIds}
              onChange={(e) =>
                setForm({
                  ...form,
                  defectFindingIds: [...e.target.selectedOptions].map((o) => o.value),
                })
              }
            >
              {data.defectFindings.map((d: any) => (
                <option key={d.id} value={d.id}>
                  {d.title}
                </option>
              ))}
            </select>
          </Feld>
          {merkantil && (
            <fieldset>
              <legend>{t('step7.merkantilLegend')}</legend>
              <div className="feld-zeile">
                <Feld
                  label={t('step7.propertySold')}
                  required
                  error={actions.fieldErrors['propertySold']}
                >
                  <select
                    value={form.propertySold}
                    onChange={(e) => setForm({ ...form, propertySold: e.target.value })}
                    required
                  >
                    <option value="">—</option>
                    <option value="ja">{t('common.yes')}</option>
                    <option value="nein">{t('common.no')}</option>
                  </select>
                </Feld>
                <Feld
                  label={t('step7.realizedLoss')}
                  required
                  error={actions.fieldErrors['realizedLossDocumented']}
                >
                  <select
                    value={form.realizedLossDocumented}
                    onChange={(e) => setForm({ ...form, realizedLossDocumented: e.target.value })}
                    required
                  >
                    <option value="">—</option>
                    <option value="ja">{t('common.yes')}</option>
                    <option value="nein">{t('common.no')}</option>
                  </select>
                </Feld>
              </div>
              <p className="hinweis">{t('scenario.merkantil.reportText')}</p>
            </fieldset>
          )}
          {(form.type === 'SCHADENERSATZ' || form.type === 'WIRTSCHAFTLICHER_VERGLEICH') && (
            <div className="feld-zeile">
              <Feld label={t('step7.amountManual')} hint={t('input.decimalHint')}>
                <input
                  value={form.amountManual}
                  onChange={(e) => setForm({ ...form, amountManual: e.target.value })}
                />
              </Feld>
              <Feld label={t('step7.repairComponent')} hint={t('step7.repairComponentHint')}>
                <input
                  value={form.repairCostComponent}
                  onChange={(e) => setForm({ ...form, repairCostComponent: e.target.value })}
                />
              </Feld>
            </div>
          )}
          <Feld label={t('step2.notes')}>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Feld>
          <Fehler code={actions.errorCode} />
          <div className="aktionen">
            <button type="submit" className="knopf-primaer">
              {editId ? t('common.save') : t('common.add')}
            </button>
            {editId && (
              <button
                type="button"
                className="knopf-sekundaer"
                onClick={() => {
                  setEditId(null);
                  setForm(leer());
                }}
              >
                {t('common.cancel')}
              </button>
            )}
          </div>
        </form>
      )}
    </section>
  );
}

function BewertungsauftragKarte({ caseId, data, reload, readonly }: StepProps) {
  const actions = useEntityActions(caseId, 'valuation-models', reload);
  function leerVm() {
    return {
      claimScenarioId: '',
      referenceObject: '',
      priceRelevant: '',
      priceDerivation: '',
      valuationDate: '',
      mwstStatus: 'UNKLAR',
      repairCost: '',
      repairCostSource: '',
      residualValueClaimedPositive: false,
    };
  }
  const [form, setForm] = useState(leerVm());
  const [editId, setEditId] = useState<string | null>(null);
  const [priceError, setPriceError] = useState<string | undefined>();
  const [derivationError, setDerivationError] = useState<string | undefined>();

  function startEdit(vm: any) {
    setEditId(vm.id);
    setForm({
      claimScenarioId: vm.claimScenarioId ?? '',
      referenceObject: vm.referenceObject ?? '',
      priceRelevant: vm.priceRelevant ?? '',
      priceDerivation: vm.priceDerivation ?? '',
      valuationDate: vm.valuationDate ? vm.valuationDate.slice(0, 10) : '',
      mwstStatus: vm.mwstStatus ?? 'UNKLAR',
      repairCost: vm.repairCost ?? '',
      repairCostSource: vm.repairCostSource ?? '',
      residualValueClaimedPositive: Boolean(vm.residualValueClaimedPositive),
    });
  }

  async function add(e: FormEvent) {
    e.preventDefault();
    let price: string | undefined;
    if (form.priceRelevant) {
      const parsed = parseDecimalInput(form.priceRelevant);
      if (parsed === null) {
        setPriceError(t('input.decimalInvalid'));
        return;
      }
      price = parsed;
    }
    // Relevanter Preis P verlangt eine Herleitung (Auftrag 7.7)
    if (price && !form.priceDerivation.trim()) {
      setDerivationError(t('step7.priceDerivationRequired'));
      return;
    }
    setDerivationError(undefined);
    setPriceError(undefined);
    const repair = form.repairCost ? parseDecimalInput(form.repairCost) : undefined;
    const body = {
      claimScenarioId: form.claimScenarioId || undefined,
      referenceObject: form.referenceObject || undefined,
      priceRelevant: price,
      priceDerivation: form.priceDerivation || undefined,
      valuationDate: form.valuationDate || undefined,
      mwstStatus: form.mwstStatus,
      repairCost: repair ?? undefined,
      repairCostSource: form.repairCostSource || undefined,
      residualValueClaimedPositive: form.residualValueClaimedPositive,
    };
    const ok = editId ? await actions.update(editId, body) : await actions.create(body);
    if (ok) {
      setForm(leerVm());
      setEditId(null);
    }
  }

  return (
    <section className="karte">
      <h2>
        {t('step7.valuationModels')}
        <InfoTip termKey="minderung" />
      </h2>
      <p className="erklaerung">{t('step7.valuationIntro')}</p>
      {data.valuationModels.length === 0 && <div className="leer-hinweis">{t('step7.valuationEmpty')}</div>}
      {data.valuationModels.map((vm: any) => (
        <div className="eintrag" key={vm.id}>
          <div className="eintrag-kopf">
            <strong>{vm.referenceObject ?? t('step7.valuationModel')}</strong>
            <span className="badge">{t('step7.methodRelative')}</span>
            {!readonly && (
              <span style={{ display: 'flex', gap: '0.4rem' }}>
                <button type="button" className="knopf-sekundaer knopf-klein" onClick={() => startEdit(vm)}>
                  {t('common.edit')}
                </button>
                <button type="button" className="knopf-sekundaer knopf-klein" onClick={() => actions.remove(vm.id)}>
                  {t('common.delete')}
                </button>
              </span>
            )}
          </div>
          <p className="meta">
            {vm.priceRelevant ? `P = ${formatCHF(vm.priceRelevant)}` : t('step7.priceMissing')}
            {vm.repairCost ? ` · ${t('step7.repairCost')}: ${formatCHF(vm.repairCost)}` : ''}
            {` · ${t('step7.mwst')}: ${t(`mwst.${vm.mwstStatus}`)}`}
            {vm.claimScenarioId
              ? ` · ${t('step7.scenarioRef')}: ${
                  data.claimScenarios.find((s: any) => s.id === vm.claimScenarioId)?.title ?? '?'
                }`
              : ''}
          </p>
          {vm.priceDerivation && (
            <p className="meta">
              {t('step7.priceDerivation')}: {vm.priceDerivation}
            </p>
          )}
        </div>
      ))}
      {!readonly && (
        <form onSubmit={add}>
          <h3>{editId ? t('step7.editValuationModel') : t('step7.addValuationModel')}</h3>
          <div className="feld-zeile">
            <Feld label={t('step7.scenarioRef')}>
              <select
                value={form.claimScenarioId}
                onChange={(e) => setForm({ ...form, claimScenarioId: e.target.value })}
              >
                <option value="">—</option>
                {data.claimScenarios.map((s: any) => (
                  <option key={s.id} value={s.id}>
                    {s.title}
                  </option>
                ))}
              </select>
            </Feld>
            <Feld label={t('step7.referenceObject')} hint={t('step7.referenceObjectHint')}>
              <input
                value={form.referenceObject}
                onChange={(e) => setForm({ ...form, referenceObject: e.target.value })}
              />
            </Feld>
          </div>
          <div className="feld-zeile">
            <Feld
              label={t('step7.priceRelevant')}
              hint={t('input.decimalHint')}
              error={priceError ?? actions.fieldErrors['priceRelevant']}
            >
              <input
                value={form.priceRelevant}
                onChange={(e) => setForm({ ...form, priceRelevant: e.target.value })}
              />
            </Feld>
            <Feld label={t('step7.valuationDate')}>
              <input
                type="date"
                value={form.valuationDate}
                onChange={(e) => setForm({ ...form, valuationDate: e.target.value })}
              />
            </Feld>
            <Feld label={t('step7.mwst')} required>
              <select
                value={form.mwstStatus}
                onChange={(e) => setForm({ ...form, mwstStatus: e.target.value })}
              >
                {MWST.map((m) => (
                  <option key={m} value={m}>
                    {t(`mwst.${m}`)}
                  </option>
                ))}
              </select>
            </Feld>
          </div>
          <Feld
            label={t('step7.priceDerivation')}
            required={Boolean(form.priceRelevant)}
            hint={t('step7.priceDerivationHint')}
            error={derivationError}
          >
            <textarea
              value={form.priceDerivation}
              onChange={(e) => setForm({ ...form, priceDerivation: e.target.value })}
            />
          </Feld>
          <div className="feld-zeile">
            <Feld label={t('step7.repairCost')} hint={t('input.decimalHint')} termKey="nachbesserungskosten">
              <input
                value={form.repairCost}
                onChange={(e) => setForm({ ...form, repairCost: e.target.value })}
              />
            </Feld>
            <Feld label={t('step7.repairCostSource')}>
              <input
                value={form.repairCostSource}
                onChange={(e) => setForm({ ...form, repairCostSource: e.target.value })}
              />
            </Feld>
          </div>
          <div className="kontrollzeile">
            <input
              id="residual"
              type="checkbox"
              checked={form.residualValueClaimedPositive}
              onChange={(e) => setForm({ ...form, residualValueClaimedPositive: e.target.checked })}
            />
            <label htmlFor="residual">{t('step7.residualClaimed')}</label>
          </div>
          <Fehler code={actions.errorCode} />
          <div className="aktionen">
            <button type="submit" className="knopf-primaer">
              {editId ? t('common.save') : t('common.add')}
            </button>
            {editId && (
              <button
                type="button"
                className="knopf-sekundaer"
                onClick={() => {
                  setEditId(null);
                  setForm(leerVm());
                }}
              >
                {t('common.cancel')}
              </button>
            )}
          </div>
        </form>
      )}
    </section>
  );
}
